import { useField, useFormikContext } from "formik";
import React, { FC, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { DEF_PG_SIZE_DROP_DOWN } from '../../constants/maximum-page-size';
import { useLocale } from "../../contexts/localeContext";
import { PaginationContext } from "../../contexts/table/PaginationContext";
import { TranslationKeys } from "../../contexts/TranslationKeys";
import deleteIcon from '../../images/Delete.svg';
import arrowIcon from '../../images/Down Arrow.svg';
import { ListQuery } from "../../models/ListQuery";
import { Name, NameWrapper } from "../../models/Name";
import { PaginationModel } from "../../models/Pagination";
import { SearchTerm } from '../../models/search/SearchTerm';
import { appendFilter } from "../../utilities/filters";
import { requiredValidation } from "../../utilities/Validation";
import Card from "../card/card";
import { toast } from "react-toastify";
import PaginatedSearchableList from "./paginated-searchable-list";

interface ParentField {
    name: string;
    accessor: string;
    key: string;
    value: string;
    childAcceccor: string;
}

interface Option {
    value: string,
    label: string
}

interface PaginatedSearchableSelectFieldProps {
    name: string;
    dataMethod: (listParameter: ListQuery) => Promise<{ rows: Name[]; pagination: PaginationModel }>;
    accessor: string;
    searchAccessor: string;
    disabled?: boolean;
    onSelect: (event, item?) => void;
    dependent?: ParentField;
    pageSize?: number;
    selectedItemLabel?: string;
    onDelete?: (event) => void;
    required?: boolean;
    customSelectedOption?: string;// Set this value when you are using this component as an individual component without formik
    customerWidth?: string //Set custom width of component e.g min-width-20
    dataCy?: string;
}

const PaginatedSearchableSelectField: FC<PaginatedSearchableSelectFieldProps> = ({ name, accessor, dataMethod, searchAccessor, disabled = false, onSelect, dependent, pageSize = DEF_PG_SIZE_DROP_DOWN, selectedItemLabel, onDelete, required = false, customSelectedOption, customerWidth, dataCy }) => {
    const [open, setOpen] = useState<boolean>(false);
    const [items, setItems] = useState<Name[]>([]);
    const [menuPosition, setMenuPosition] = useState<{ top: number; left: number; width: number }>({ top: 0, left: 0, width: 0 });
    const [selectedOption, setSelectedOption] = useState<Name>({
        label: "",
        value: "",
    });
    const [pagination, setPagination] = useState<PaginationModel>({
        maxPageSize: pageSize,
        totalCount: 0,
        pageNumber: 1,
        totalPages: 0,
    });
    const [selectedItems, setSelectedItems] = useState<number[]>([]);
    const { values } = useFormikContext<any>();
    const initialOption = { value: "", label: "" };
    const { translate } = useLocale();
    const [field] = useField({
        name: accessor,
        validate: required ? (value) => requiredValidation(value, translate) : undefined,
    });
    const initialSearchTerm: SearchTerm = {
        accessor: searchAccessor,
        translationKey: "name",
        value: "",
    };
    const [searchTerm, setSearchTerm] = useState<SearchTerm>(initialSearchTerm);
    const wrapperRef = useRef<HTMLDivElement | null>(null);
    const buttonRef = useRef<HTMLButtonElement | null>(null);
    const dropdownRef = useRef<HTMLDivElement | null>(null);

    const refreshData = useCallback((listParameter: ListQuery) => {
        dataMethod(listParameter)
            .then((response: NameWrapper) => {
                setItems(response.rows);
                setPagination(response.pagination);
            })
            .catch(() => {
                toast.warn(`${translate.byKey(TranslationKeys.loadSelectOptionFailed)} ${name}`);
            });
    },
        [dataMethod, name, translate],
    );

    useEffect(() => {
        if (open) {
            const listParameter: ListQuery = {
                goToPage: pagination.pageNumber,
                pageSize: pagination.maxPageSize,
                searchTerm: searchTerm,
                filters: [],
            };

            if (dependent && accessor === dependent.childAcceccor) {
                listParameter.filters = appendFilter(
                    dependent.value,
                    dependent.name,
                    dependent.key,
                    listParameter.filters!,
                );
            }

            refreshData(listParameter);
        }
    }, [open, dependent, accessor, pagination.pageNumber, pagination.maxPageSize, searchTerm]);

    useLayoutEffect(() => {
        if (!open) {
            return;
        }

        const updateMenuPosition = () => {
            const buttonRect = buttonRef.current?.getBoundingClientRect();
            if (!buttonRect) {
                return;
            }

            setMenuPosition({
                top: buttonRect.bottom + 4,
                left: buttonRect.left,
                width: buttonRect.width,
            });
        };

        updateMenuPosition();
        window.addEventListener('resize', updateMenuPosition);
        window.addEventListener('scroll', updateMenuPosition, true);

        const handleMouseDown = (event: MouseEvent) => {
            const target = event.target as Node;
            if (wrapperRef.current?.contains(target) || dropdownRef.current?.contains(target)) {
                return;
            }

            onClose();
        };

        document.addEventListener('mousedown', handleMouseDown);

        return () => {
            window.removeEventListener('resize', updateMenuPosition);
            window.removeEventListener('scroll', updateMenuPosition, true);
            document.removeEventListener('mousedown', handleMouseDown);
        };
    }, [open]);

    useEffect(() => {
        findSelectedOption(customSelectedOption ?? get(values, accessor));
    }, [values, items, customSelectedOption, accessor, selectedItemLabel]);

    const findSelectedOption = useCallback((value) => {
        const newSelectedOption: Option = items.find((option) => value === option.value) ?? initialOption;
        if (newSelectedOption === initialOption && value && selectedItemLabel) {
            const selected: Option = { value: value, label: selectedItemLabel ?? "" };
            setItems((prevItems) => searchTerm.value ? prevItems : [selected, ...prevItems]);
            setSelectedOption(selected);
            setSelectedItems([value]);
        } else if (newSelectedOption.label.length > 0 && selectedItemLabel != newSelectedOption.label && selectedItemLabel) {
            newSelectedOption.label = selectedItemLabel ?? newSelectedOption.label;
            setSelectedOption(newSelectedOption);
            setSelectedItems([value]);
        } else {
            setSelectedOption(newSelectedOption);
            setSelectedItems([value]);
        }
    }, [items, initialOption, values, selectedItemLabel]);

    const handleItemSelected = (index: number, item: Name) => {
        setSelectedOption(items[index]);
        setSelectedItems([index]);
        onSelect(item, items[index]);
        onClose();
    };

    const onSearch = (value: string) => {
        setSearchTerm((prev) => ({ ...prev, value }));
        setOpen(true);
    };

    const updatePageNumber = (pageNumber: number) => {
        setPagination((prev) => ({ ...prev, pageNumber }));
        setOpen(true);
    };

    const updatePageSize = (pageSize: number) => {
        setPagination((prev) => ({
            ...prev,
            maxPageSize: pageSize,
            pageNumber: 1,
        }));
        setOpen(true);
    };

    const onClose = () => {
        setOpen(false);
        setSearchTerm(initialSearchTerm);
    };

    /**
     * Utility function to get the value from nested objects based on accessor string.
     * 
     * @param values 
     * @param accessor 
     * @returns 
     */
    const get = (values: any, accessor: string) => {
        return accessor.split('.').reduce((acc, part) => acc && acc[part], values);
    }

    return (
        <PaginationContext.Provider value={{ isInUse: true, pagination, updatePageNumber, updatePageSize }}>
            <div ref={wrapperRef} className="flex space-x-1">
                    <button
                        ref={buttonRef}
                        onClick={() => setOpen(!open)}
                        type="button"
                        disabled={disabled}
                        className={`bg-gray-input p-1 px-2 text-left flex flex-row justify-between items-start text-sm truncate ${customerWidth ? customerWidth : "w-full"}  ${disabled ? 'cursor-not-allowed text-gray-500' : 'text-gray-900'}`}
                        id="PaginatedSearchableListBtn"
                        data-cy={dataCy}>
                        {selectedOption.label || "-"}
                        <img className="pl-2 select-field--arrow" src={arrowIcon} alt="Arrow Icon" />
                    </button>
                    {onDelete &&
                        <div className="flex relative w-6 ml-1" onClick={onDelete}>
                            <div className="absolute bottom-0 mb-1 right-0 cursor-pointer">
                                <img src={deleteIcon} alt="Delete Icon" />
                            </div>
                        </div>}
                {open && typeof document !== 'undefined' && createPortal(
                    <div
                        ref={dropdownRef}
                        className="position-float-modal"
                        style={{ position: 'fixed', top: menuPosition.top, left: menuPosition.left, width: menuPosition.width }}>
                        <Card className="bg-white px-2 py-2 selection-content-list max-h-80 overflow-y-auto">
                            <PaginatedSearchableList
                                items={items}
                                onSelect={handleItemSelected}
                                searchable={true}
                                onSearch={onSearch}
                                selectedItems={selectedItems} />
                        </Card>
                    </div>,
                    document.body
                )}
            </div>
        </PaginationContext.Provider>
    );
};

export default PaginatedSearchableSelectField;
